import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { resolveMediaUrl } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";
import LanguageSwitcher from "@/components/site/LanguageSwitcher";
import { useProductMenu } from "@/hooks/useProductMenu";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [drop, setDrop] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { settings } = useSettings();
  const { t, lang } = useLanguage();
  const s = settings || {};
  const products = useProductMenu(lang);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 6);
    window.addEventListener("scroll", h);
    h();
    return () => window.removeEventListener("scroll", h);
  }, []);
  useEffect(() => { setOpen(false); setDrop(false); }, [pathname]);

  const on = (to) => (to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(`${to}/`));
  const cls = (to) => `sas-navlink ${on(to) ? "is-on" : ""}`;

  return (
    <header data-testid="site-navbar" className={`sticky top-0 z-40 sas-nav ${scrolled ? "shadow-md" : ""}`}>
      <div className="sas-wrap flex items-center justify-between h-[78px] lg:h-[86px] gap-3">
        <Link to="/" data-testid="nav-logo" className="shrink-0">
          {s.logo ? (
            <img src={resolveMediaUrl(s.logo)} alt={s.site_name || "Logo"} className="h-12 sm:h-14 lg:h-[58px] max-w-[220px] w-auto object-contain object-left" />
          ) : (
            <span className="font-bold text-[#e82020] text-lg">Mandala</span>
          )}
        </Link>

        <nav className="hidden xl:flex items-center">
          <Link to="/" className={cls("/")}>{t("nav.home")}</Link>
          <Link to="/tentang" className={cls("/tentang")}>{t("nav.about")}</Link>
          <div className="relative" onMouseEnter={() => setDrop(true)} onMouseLeave={() => setDrop(false)}>
            <Link to="/produk" className={`${cls("/produk")} inline-flex items-center gap-1`}>
              {t("nav.products")} <ChevronDown size={13} />
            </Link>
            {drop && (
              <div className="sas-drop">
                {products.map((m) => <Link key={m.label} to={m.to}>{m.label}</Link>)}
              </div>
            )}
          </div>
          <Link to="/layanan" className={cls("/layanan")}>{t("nav.services")}</Link>
          <Link to="/order-sample" className={cls("/order-sample")}>{t("nav.orderSample")}</Link>
          <Link to="/galeri" className={cls("/galeri")}>{t("nav.gallery")}</Link>
          <Link to="/faq" className={cls("/faq")}>{t("nav.faq")}</Link>
          <Link to="/karir" className={cls("/karir")}>{t("nav.career")}</Link>
          <Link to="/kontak" className={cls("/kontak")}>{t("nav.contact")}</Link>
          <Link to="/live" className={cls("/live")}>{t("nav.live")}</Link>
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <button data-testid="nav-toggle" className="xl:hidden p-2 text-[#e82020]" onClick={() => setOpen((v) => !v)} aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-white border-t border-[rgba(232,32,32,0.16)] max-h-[80vh] overflow-auto">
          <div className="sas-wrap py-3 flex flex-col">
            <Link to="/" className="sas-navlink">{t("nav.home")}</Link>
            <Link to="/tentang" className="sas-navlink">{t("nav.about")}</Link>
            <Link to="/produk" className="sas-navlink">{t("nav.products")}</Link>
            {products.map((m) => <Link key={m.label} to={m.to} className="sas-navlink pl-6">{m.label}</Link>)}
            <Link to="/layanan" className="sas-navlink">{t("nav.services")}</Link>
            <Link to="/order-sample" className="sas-navlink">{t("nav.orderSample")}</Link>
            <Link to="/galeri" className="sas-navlink">{t("nav.gallery")}</Link>
            <Link to="/faq" className="sas-navlink">{t("nav.faq")}</Link>
            <Link to="/karir" className="sas-navlink">{t("nav.career")}</Link>
            <Link to="/kontak" className="sas-navlink">{t("nav.contact")}</Link>
            <Link to="/live" className="sas-navlink">{t("nav.live")}</Link>
            <Link to="/artikel" className="sas-navlink">{t("nav.articles")}</Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
